import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    // Single delegated listener — survives all innerHTML replacements inside the drawer.
    this.addEventListener('click', this.#handleDelegatedClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('click', this.#handleDelegatedClick);
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) this.showDialog();
    this.fetchAndRender();
  };

  /**
   * Delegated click handler for all interactive buttons inside the drawer.
   * Attached once on the component root — never lost on innerHTML changes.
   */
  #handleDelegatedClick = (e) => {
    const qtyBtn = e.target.closest('.cart-drawer__qty-btn');
    if (qtyBtn) {
      e.stopPropagation();
      e.preventDefault();
      this.#changeQty(qtyBtn.dataset.id, parseInt(qtyBtn.dataset.qty, 10));
      return;
    }

    const removeBtn = e.target.closest('.cart-drawer__item-remove');
    if (removeBtn) {
      e.stopPropagation();
      e.preventDefault();
      this.#changeQty(removeBtn.dataset.id, 0);
    }
  };

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  open() {
    this.showDialog();
    this.fetchAndRender();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // ---------------------------------------------------------------------------
  // Data fetching — used on drawer open and cart add
  // ---------------------------------------------------------------------------

  async fetchAndRender() {
    const cartItemsEl = this.querySelector('cart-items-component');

    // When the cart starts empty, Liquid renders a minimal structure with no
    // trust strip and no summary. renderItems() can't patch into this shell.
    // Fetch the section to rebuild the filled-cart structure first.
    if (cartItemsEl && !this.querySelector('.cart-drawer__trust-strip')) {
      try {
        const sectionId = cartItemsEl.dataset.sectionId;
        const res  = await fetch(`/?sections=${sectionId}`);
        const json = await res.json();
        const doc  = new DOMParser().parseFromString(json[sectionId], 'text/html');
        const newContent = doc.querySelector('cart-items-component');
        if (newContent) {
          cartItemsEl.innerHTML = newContent.innerHTML;
          this.querySelector('.cart-drawer__dialog')?.classList.remove('cart-drawer--empty');
        }
      } catch (_) {}
    }

    // Capture compare prices from the current DOM before replacing it.
    // Liquid uses item.variant.compare_at_price (correct); /cart.js may omit it.
    const comparePriceMap = {};
    this.querySelectorAll('[data-key]').forEach(el => {
      const key = el.dataset.key;
      const src = el.querySelector('.compare-at-price, .cart-drawer__item-compare');
      if (src) {
        const cents = Math.round(parseFloat(src.textContent.replace(/[^\d.]/g, '')) * 100);
        if (cents > 0) comparePriceMap[key] = cents;
      }
    });

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.renderItems(cart, comparePriceMap);
      this.updateTotals(cart);
    } catch (_) {
      // fail silently — native Liquid rendering stays visible on error
    }
  }

  // ---------------------------------------------------------------------------
  // Full render — only called on initial load or when cart becomes empty
  // ---------------------------------------------------------------------------

  renderItems(cart, comparePriceMap = {}) {
    const container = this.querySelector('.cart-drawer__items');
    if (!container) return;

    const dialog       = this.querySelector('.cart-drawer__dialog');
    const heading      = this.querySelector('.cart-drawer__heading');
    const summary      = this.querySelector('.cart-drawer__summary');
    const trustStrip   = this.querySelector('.cart-drawer__trust-strip');
    const drawerBubble = this.querySelector('.cart-drawer__dialog .cart-bubble');
    const closeBtn     = this.querySelector('.cart-drawer__close-button');

    if (cart.item_count === 0) {
      dialog?.classList.add('cart-drawer--empty');
      container.innerHTML = '<p class="cart-drawer__empty">DEIN WARENKORB IST LEER</p>';
      if (heading)      heading.style.display      = 'none';
      if (summary)      summary.style.display      = 'none';
      if (trustStrip)   trustStrip.style.display   = 'none';
      if (drawerBubble) drawerBubble.style.display  = 'none';
      if (closeBtn)     closeBtn.style.display      = 'none';
      return;
    }

    dialog?.classList.remove('cart-drawer--empty');
    if (heading)      heading.style.display      = '';
    if (summary)      summary.style.display      = '';
    if (trustStrip)   trustStrip.style.display   = '';
    if (drawerBubble) drawerBubble.style.display  = '';
    if (closeBtn)     closeBtn.style.display      = '';

    container.innerHTML = cart.items.map(item => {
      // Strike-through price hierarchy:
      // 1. compare_at_price — "was" price from Shopify admin via cart API
      // 2. original_price   — before automatic/script discounts
      // 3. price            — listed variant price
      // 4. comparePriceMap  — captured from Liquid DOM before this render ran
      //    (Liquid has item.variant.compare_at_price; /cart.js may omit it)
      const compareFromMap = comparePriceMap[item.key] || 0;
      const strikePrice = (item.compare_at_price > item.final_price)
        ? item.compare_at_price
        : (item.original_price > item.final_price)
          ? item.original_price
          : (item.price > item.final_price)
            ? item.price
            : (compareFromMap > item.final_price)
              ? compareFromMap
              : null;
      const compareSpan = strikePrice
        ? `<span class="cart-drawer__item-compare">CHF ${(strikePrice / 100).toFixed(2)}</span>`
        : '';

      const variantLabel = item.variant_title && item.variant_title !== 'Default Title'
        ? ' · ' + item.variant_title : '';

      // type="button" is required on every button. Without it, buttons default to
      // type="submit" and trigger any ancestor <form>, navigating away and closing the drawer.
      return `
        <div class="cart-drawer__item" data-key="${item.key}">
          <img
            class="cart-drawer__item-image"
            src="${item.image}"
            alt="${item.product_title}"
            loading="lazy"
          >
          <div class="cart-drawer__item-info">
            <a class="cart-drawer__item-title" href="${item.url}">${item.product_title}${variantLabel}</a>
            <div class="cart-drawer__item-pricing">
              <span class="cart-drawer__item-price">CHF ${(item.final_price / 100).toFixed(2)}</span>
              ${compareSpan}
            </div>
            <div class="cart-drawer__qty-row">
              <div class="cart-drawer__qty">
                <button type="button" class="cart-drawer__qty-btn"
                  data-id="${item.key}" data-qty="${item.quantity - 1}">−</button>
                <span class="cart-drawer__qty-value">${item.quantity}</span>
                <button type="button" class="cart-drawer__qty-btn"
                  data-id="${item.key}" data-qty="${item.quantity + 1}">+</button>
              </div>
              <button type="button" class="cart-drawer__item-remove"
                data-id="${item.key}">Entfernen</button>
            </div>
          </div>
        </div>
      `;
    }).join('');
    // No inline addEventListener — #handleDelegatedClick on the root handles all clicks.
  }

  updateTotals(cart) {
    const formatted = 'CHF ' + (cart.total_price / 100).toFixed(2);

    const checkoutBtn = this.querySelector('.cart__checkout-button');
    if (checkoutBtn) {
      if (cart.item_count === 0) {
        checkoutBtn.style.display = 'none';
      } else {
        checkoutBtn.style.display = '';
        checkoutBtn.textContent   = 'Sicher zur Kasse · ' + formatted;
        checkoutBtn.setAttribute('type', 'button');
        checkoutBtn.onclick = (e) => {
          e.stopPropagation();
          e.preventDefault();
          window.location.href = '/checkout';
        };
      }
    }

    const subtotalEl = this.querySelector('[data-testid="cart-subtotal-value"]');
    if (subtotalEl) subtotalEl.textContent = formatted;

    const totalEl = this.querySelector('[ref="cartTotal"]');
    if (totalEl) totalEl.textContent = formatted;
  }

  // ---------------------------------------------------------------------------
  // Cart mutation
  // ---------------------------------------------------------------------------

  async #changeQty(id, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ id, quantity })
      });
      const cart = await response.json();

      if (cart.item_count === 0) {
        // Full re-render only needed for the empty-cart state transition.
        this.renderItems(cart);
      } else {
        // Targeted patch: only update the values that changed.
        // No full innerHTML reset → no flicker, no layout shift.
        this.#patchItem(id, cart);
      }

      this.updateTotals(cart);

      // Notify cart-icon.js so the header bubble count stays in sync.
      //
      // ROOT CAUSE OF THE FLICKER:
      // cart-items-component listens to CartUpdateEvent on document. When the event
      // carries no `sections` data, its handler falls to:
      //   sectionRenderer.renderSection(this.sectionId, { cache: false })
      // — a full async Shopify section re-render that replaces our custom HTML with
      // the native Liquid table, causing the drawer to flash back to the old layout.
      //
      // FIX: dispatch from the cart-items-component element rather than document.
      // cart-items-component's handler checks `if (event.target === this) return`
      // — dispatching from the element causes this guard to fire, preventing the
      // section re-render entirely. The event still bubbles up to document, so
      // cart-icon.js receives it and updates the header bubble normally.
      const cartItemsEl = this.querySelector('cart-items-component');
      const dispatchTarget = cartItemsEl ?? document;
      dispatchTarget.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-drawer', { itemCount: cart.item_count })
      );
    } catch (err) {
      console.error('cart-drawer changeQty error:', err);
    }
  }

  /**
   * Surgically updates a single cart item in the DOM without replacing any other nodes.
   * @param {string} id - The line item key.
   * @param {object} cart - The updated cart from /cart/change.js.
   */
  #patchItem(id, cart) {
    const itemEl = this.querySelector(`.cart-drawer__item[data-key="${id}"]`);
    const updatedItem = cart.items.find(item => item.key === id);

    if (!updatedItem) {
      // Item was removed (quantity reached 0) — remove its DOM node.
      itemEl?.remove();
      return;
    }

    if (!itemEl) return;

    // Update quantity display
    const qtyValue = itemEl.querySelector('.cart-drawer__qty-value');
    if (qtyValue) qtyValue.textContent = String(updatedItem.quantity);

    // Update data-qty on − and + so the next click carries the correct value
    const [minusBtn, plusBtn] = itemEl.querySelectorAll('.cart-drawer__qty-btn');
    if (minusBtn) minusBtn.dataset.qty = String(updatedItem.quantity - 1);
    if (plusBtn)  plusBtn.dataset.qty  = String(updatedItem.quantity + 1);

    // Update unit price
    const priceEl = itemEl.querySelector('.cart-drawer__item-price');
    if (priceEl) priceEl.textContent = `CHF ${(updatedItem.final_price / 100).toFixed(2)}`;

    // Update compare price (may appear or disappear if a discount threshold is crossed)
    const pricingEl       = itemEl.querySelector('.cart-drawer__item-pricing');
    const existingCompare = itemEl.querySelector('.cart-drawer__item-compare');
    const strikePrice     = (updatedItem.compare_at_price > updatedItem.final_price)
      ? updatedItem.compare_at_price
      : (updatedItem.original_price > updatedItem.final_price)
        ? updatedItem.original_price
        : (updatedItem.price > updatedItem.final_price)
          ? updatedItem.price
          : null;

    if (strikePrice) {
      const compareText = `CHF ${(strikePrice / 100).toFixed(2)}`;
      if (existingCompare) {
        existingCompare.textContent = compareText;
      } else if (pricingEl) {
        const span = document.createElement('span');
        span.className   = 'cart-drawer__item-compare';
        span.textContent = compareText;
        pricingEl.appendChild(span);
      }
    }
    // Do NOT remove existingCompare when strikePrice is null.
    // compare_at_price is a variant property — it does not change with quantity.
    // /cart/change.js may omit the field entirely, so null here means "unknown", not "absent".
  }

  // ---------------------------------------------------------------------------
  // Sticky summary state
  // ---------------------------------------------------------------------------

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight  = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
